/**
 * Cell renderers for the 项目参数 table — the visual layer on top of the plain
 * formatting in `utils/programs`.
 */
import { Box, Icon, Link } from '@amzn/awsui-components-react';

import { UserAvatar } from '../../../eventHub/components/UserAvatar';
import type { DiscountMethod } from '../../client/CpaClient';
import { type Contact, percent, ratioBounds } from '../../utils/programs';
import './ProgramsPage.css';

/** Placeholder for an absent value: a short, muted dash. */
export function Empty() {
  return (
    <Box color="text-status-inactive" variant="span">
      -
    </Box>
  );
}

const PHONETOOL_URL = 'https://phonetool.amazon.com/users/';

/** Priority as a plain bold number, like the table's other numeric columns. */
export function Priority({ priority }: { priority: number | undefined }) {
  if (priority == null) return <Empty />;
  return <span className="cpa-programs-priority">{priority}</span>;
}

/** A person as badge photo + login linking to Phone Tool; a contact with no login stays plain text. */
export function People({ contacts }: { contacts: Contact[] }) {
  if (contacts.length === 0) return <Empty />;
  return (
    <span className="cpa-programs-people">
      {contacts.map((c) =>
        c.login ? (
          <a
            key={c.login}
            className="cpa-programs-person"
            href={`${PHONETOOL_URL}${encodeURIComponent(c.login)}`}
            target="_blank"
            rel="noopener noreferrer"
            title={c.name}
          >
            <UserAvatar login={c.login} size={20} />
            {c.login}@
          </a>
        ) : (
          <Box key={c.name} color="text-body-secondary" variant="span">
            {c.name}
          </Box>
        ),
      )}
    </span>
  );
}

/**
 * Discount ratio, styled like the amount column: the range in bold, or a plain
 * label for "no limit" and for a tier table (whose tiers are confidential).
 */
export function Discount({
  method,
  noLimitLabel,
  tieredLabel,
}: {
  method: DiscountMethod | undefined;
  noLimitLabel: string;
  tieredLabel: string;
}) {
  if (method?.discountType === 'no_limit') return <>{noLimitLabel}</>;
  if (method?.discountType === 'table') return <>{tieredLabel}</>;
  const bounds = ratioBounds(method);
  if (!bounds) return <Empty />;
  return (
    <span className="cpa-programs-num">
      {bounds.min === bounds.max
        ? `${percent(bounds.min)} %`
        : `${percent(bounds.min)} – ${percent(bounds.max)} %`}
    </span>
  );
}

/** Program name (its id only when it has no name), with a Wiki icon link when one exists. */
export function ProgramName({ name, programId, wiki }: { name?: string; programId?: string; wiki?: string }) {
  const label = name ?? programId;
  // With a wiki the name itself is the link (opens in a new tab), rather than a separate icon beside it.
  return (
    <span className="cpa-programs-name">
      {wiki ? (
        <Link variant="primary" href={wiki} target="_blank">
          {label}
        </Link>
      ) : (
        <Box fontWeight="bold" variant="span">
          {label}
        </Box>
      )}
    </span>
  );
}

/** Cycle with an icon: a loop for recurring, a single mark for one-time. */
export function Cycle({ cycle, label }: { cycle: string | undefined; label: string | undefined }) {
  if (!label) return <Empty />;
  return (
    <span className="cpa-programs-cycle">
      <Icon name={cycle === 'recurring' ? 'refresh' : 'calendar'} size="small" />
      {label}
    </span>
  );
}

/** SLA with a clock icon. */
export function Sla({ sla }: { sla: string | undefined }) {
  if (!sla) return <Empty />;
  return (
    <span className="cpa-programs-cycle">
      <Icon name="status-pending" size="small" />
      {sla}
    </span>
  );
}
